import { useState } from "react";
import { api, ApiError } from "../api";
import { Term } from "./Term";

// "Check for new student work" — what a sync would do, before it does it.
//
// A Google Doc has no submission event. A student who was still drafting when the folder was read
// is late and changed at the same moment, and nothing about the file says which. So the teacher is
// never asked to classify anything: they are shown what arrived, what changed, and what taking the
// newer version would cost.
//
// WHAT THIS IS NOT. It is not a gate. Nothing here can refuse. The newer text is what the student
// actually wrote, and a system that made a teacher argue with it would be wrong about whose work
// it is. The warnings exist so the decision is informed, not so it is hard.
//
// Nothing is deleted either way. A changed paper produces a new artifact that supersedes the old
// one, and the old keeps its scores, its reviewer and its delivery record.

export interface NewFile {
  file_id: string;
  name: string;
  student_id: string | null;
  named: boolean;
}

export interface ChangedFile extends NewFile {
  artifact_id: string;
  /** Scores on the version being superseded. */
  scores: number;
  /** The teacher said send it. */
  released: boolean;
  /** It has actually gone to the student. */
  delivered: boolean;
}

export interface IncomingView {
  available: boolean;
  new: NewFile[];
  changed: ChangedFile[];
  unchanged: number;
  awaiting_confirmation: { manifest_id: string; folder: string; files: number }[];
  /** Files that were in the folder and will never become papers — a PDF or a photograph this
   *  cannot open (`unreadable`), or a document it judged to be the assignment itself
   *  (`not_student_work`). Listed rather than counted, so a name a teacher recognises is on the
   *  screen beside the reason. */
  not_taken?: { file_id: string; name: string; status: string; why: string | null }[];
}

/** What taking this version costs, in the order it matters. Feedback already with a student is a
 *  different weight from a score only the teacher has seen. */
function cost(c: ChangedFile): string | null {
  if (c.delivered) return "feedback is already with the student";
  if (c.released) return "you already said send this";
  if (c.scores > 0) return `${c.scores} score${c.scores === 1 ? "" : "s"} on the current version`;
  return null;
}

/** The Drive folder a collection is read from, named and editable.
 *
 *  It sits beside the check button because it is the thing the button acts on. Before this the
 *  folder came from whatever a batch script was pointed at, so "Check for new student work" was
 *  a button over a setting nobody in the building could see, let alone change.
 *
 *  EXPORTED because the lesson screen needs the same control. A collection there can show "no
 *  folder yet" — which is a state a teacher looked at with no way to fix it, having to open the
 *  collection to find the one editor that existed. One editor, in both places, rather than a
 *  second that can disagree with it about what a folder link is.
 */
export function FolderField({ activityId, url, name, onSaved, startOpen, emptyLabel }: {
  activityId: string;
  url: string | null;
  name: string | null;
  onSaved: (folder: { url: string | null; name: string | null }) => void;
  startOpen?: boolean;
  /** What the control says when there is no folder yet. It is the whole next step, not a link
   *  beside one, so it reads as a button rather than an aside. */
  emptyLabel?: string;
}) {
  const [editing, setEditing] = useState(Boolean(startOpen));
  const [draftUrl, setDraftUrl] = useState(url ?? "");
  const [draftName, setDraftName] = useState(name ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = () => {
    setSaving(true); setError(null);
    api.put<{ folder_url: string | null; folder_name: string | null }>(
      `/collections/${encodeURIComponent(activityId)}/folder`,
      { url: draftUrl, name: draftName })
      .then((r) => { onSaved({ url: r.folder_url, name: r.folder_name }); setEditing(false); })
      // The server's sentence, not a generic one: it is the message that says what is wrong with
      // the link, and a teacher can act on "that is not a folder address".
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "could not be saved"))
      .finally(() => setSaving(false));
  };

  if (!editing) {
    // Nothing set yet: the control IS "add the folder", because that is the only thing this
    // collection can do next.
    if (!url) {
      return (
        <button className="btn" onClick={() => setEditing(true)}>
          {emptyLabel ?? "Add the Google Drive folder"}
        </button>
      );
    }
    return (
      <span className="inc-folder">
        {/* DRIVE, not "Folder". The thing this sits in is already a folder of student work;
            labelling its source "Folder" is what made the two read as one object. */}
        <span className="inc-folder-k">Drive</span>
        <a href={url} target="_blank" rel="noreferrer">{name || "Drive folder"}</a>
        <button className="btn-link" onClick={() => setEditing(true)}>Change</button>
      </span>
    );
  }

  return (
    <span className="inc-folder inc-editing">
      <input value={draftName} placeholder="Name, e.g. Final drafts" disabled={saving}
             onChange={(e) => setDraftName(e.target.value)} />
      <input value={draftUrl} placeholder="Paste the Google Drive folder link" disabled={saving}
             size={38} onChange={(e) => setDraftUrl(e.target.value)} />
      <button className="btn primary" disabled={saving} onClick={save}>
        {saving ? "Saving…" : "Save"}
      </button>
      <button className="btn" disabled={saving}
              onClick={() => { setEditing(false); setError(null);
                               setDraftUrl(url ?? ""); setDraftName(name ?? ""); }}>
        Cancel
      </button>
      {error && <span className="inc-error">{error}</span>}
    </span>
  );
}

export function Incoming({ folder, onFolderSaved, onTaken }: {
  /** The collection on screen: what gets re-read, and what carries the folder. */
  folder?: { activity_id: string; url: string | null; name: string | null } | null;
  onFolderSaved?: (f: { url: string | null; name: string | null }) => void;
  onTaken?: () => void;
}) {
  const [view, setView] = useState<IncomingView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [took, setTook] = useState<string | null>(null);
  // Folders inside the one that was read. The read does not recurse, so a link pointing at a
  // parent finds only what is loose at the top level — one paper where there were twenty.
  const [notRead, setNotRead] = useState<string[]>([]);
  // Collapsed by default: it is an account of what happened, not a list of things to do.
  const [showSkipped, setShowSkipped] = useState(false);
  // CHANGED FILES A TEACHER HAS DECIDED TO LEAVE ALONE. Replace is the default because it is
  // almost always right — the newer text is what the student wrote — but a paper whose feedback is
  // already with somebody is a case for leaving the record where it is.
  const [ignored, setIgnored] = useState<Set<string>>(new Set());

  // READ THE FOLDER FIRST, then say what is there. Showing what the last batch read found while
  // the button says "check" is a stale answer wearing a fresh one's clothes.
  //
  // A re-read that cannot happen is reported and the pending work is still shown: "Drive is not
  // connected" is true and useful, and it does not mean there is nothing waiting.
  const check = () => {
    setBusy(true); setError(null); setTook(null);
    // The COLLECTION, which is what the endpoint asks for. This used to send the scope —
    // section, task and iteration — and kept sending it after the endpoint moved to activity_id,
    // so every press answered "activity_id is required". Nothing caught it: the fixture accepted
    // any payload, so the preview was happy and only production disagreed.
    setNotRead([]);
    const reread = folder?.activity_id
      ? api.post<{ folders_not_read?: string[] }>(
            "/intake/reread", { activity_id: folder.activity_id })
          .then((r) => { setNotRead(r.folders_not_read ?? []); return null; })
          .catch((e: unknown) => e instanceof ApiError ? e.message : "could not check the folder")
      : Promise.resolve("no folder is open, so nothing was checked");
    reread
      .then((why) => api.get<IncomingView>("/review/incoming")
        .then((v: IncomingView) => { setView(v); if (why) setError(why); }))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "could not be read"))
      .finally(() => setBusy(false));
  };

  const take = () => {
    if (!view) return;
    // The files the teacher was SHOWN, repeated back. A folder can be read again between looking
    // and pressing, and "everything pending" would take papers nobody saw.
    const file_ids = [...view.new, ...view.changed]
      .map((f) => f.file_id).filter((id) => !ignored.has(id));
    setBusy(true); setError(null);
    api.post<{ taken: number }>("/review/incoming/accept", { file_ids })
      .then((r: { taken: number }) => {
        setTook(`${r.taken} paper${r.taken === 1 ? "" : "s"} taken.`);
        setView(null);
        onTaken?.();
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "could not be taken"))
      .finally(() => setBusy(false));
  };

  const total = view ? view.new.length + view.changed.length : 0;
  // What pressing the button would actually take: everything found, less the changed papers a
  // teacher has chosen to leave standing.
  const taking = view
    ? [...view.new, ...view.changed].filter((f) => !ignored.has(f.file_id)).length
    : 0;

  return (
    <div className="inc">
      {/* ONE control, at whichever stage this collection is in. A collection with no folder
          cannot be checked, so offering a disabled "Check for new student work" beside an "Add
          one" link was two controls for a single next step — and the disabled one advertised a
          thing the teacher could not do. Now the button IS the next step. */}
      {folder && !folder.url ? (
        <FolderField activityId={folder.activity_id} url={folder.url} name={folder.name}
                     onSaved={(f) => onFolderSaved?.(f)} startOpen={false}
                     emptyLabel="Add the Google Drive folder" />
      ) : (
        <>
          {folder && (
            <FolderField activityId={folder.activity_id} url={folder.url} name={folder.name}
                         onSaved={(f) => onFolderSaved?.(f)} />
          )}
          <button className="btn" disabled={busy} onClick={check}>
            {busy && !view ? "Checking…" : "Check for new student work"}
          </button>
        </>
      )}
      {took && <span className="inc-took">{took}</span>}
      {error && <span className="inc-error">{error}</span>}

      {/* WHAT WAS NOT READ, next to what was. A read looks in one folder and does not recurse,
          which is a deliberate refusal to guess at a nested structure — but it used to refuse
          silently, so a link to a parent holding two folders of ten papers reported one paper and
          left the other twenty unmentioned. Said plainly, with the names, and with the one thing
          that fixes it. */}
      {notRead.length > 0 && (
        <span className="inc-warn">
          {notRead.length === 1 ? "One folder inside this one was" : `${notRead.length} folders inside this one were`}
          {" "}not read: {notRead.join(", ")}. A <Term name="manifest">read</Term> only looks in the
          folder you point at.
          Point this work at a folder that holds the papers themselves, or add each one separately.
        </span>
      )}

      {view && (
        <div className="inc-panel">
          {/* Nothing to do says so. A panel that only ever appears with work in it leaves the
              teacher unsure whether the check ran. */}
          {/* "NOTHING NEW" HAS TO MEAN NOTHING WAS THERE. A folder of twenty papers this cannot
              open produced no new, no changed, no unchanged and nothing awaiting — so it took this
              branch and said "Nothing new", which is the same wrong answer the counts were changed
              to stop giving. Files nobody can take still have to be counted before the screen is
              allowed to say there was nothing. */}
          {total === 0 && view.awaiting_confirmation.length === 0
             && (view.not_taken?.length ?? 0) === 0 ? (
            <p className="inc-quiet">
              Nothing new. {view.unchanged > 0 && `${view.unchanged} unchanged since the last read.`}
            </p>
          ) : (
            <>
              {/* THREE OUTCOMES, ALWAYS NAMED. A re-read finds a file unchanged, changed, or
                  new — `decide` has exactly these three answers, compared on the TEXT rather than
                  on a timestamp, because Drive bumps `modified_at` when a student opens a document
                  and changes nothing. Unchanged is COUNTED and not listed: hiding it entirely is
                  how a folder of twenty once reported "Nothing new". */}
              <p className="inc-counts">
                <b>{view.new.length} new</b>
                {view.changed.length > 0 && <> · <b>{view.changed.length} changed</b></>}
                {view.unchanged > 0 && <span className="inc-quiet"> · {view.unchanged} unchanged</span>}
                {(view.not_taken?.length ?? 0) > 0 && (
                  <span className="inc-quiet"> · {view.not_taken!.length} not taken</span>
                )}
              </p>

              {view.changed.length > 0 && (
                <ul className="inc-list">
                  {view.changed.map((c) => {
                    const skip = ignored.has(c.file_id);
                    return (
                      <li key={c.file_id} className={skip ? "inc-ignored" : ""}>
                        <span className="inc-name">{c.name}</span>
                        {/* Named, because "2 have feedback out" is not something anyone can
                            act on. */}
                        {cost(c) && <span className="inc-cost">{cost(c)}</span>}
                        {/* PER ROW, because the exceptions are per paper — and one press still
                            handles the common case, where every changed paper should be taken. */}
                        <button className="btn-link" onClick={() => setIgnored((was) => {
                          const next = new Set(was);
                          if (skip) next.delete(c.file_id); else next.add(c.file_id);
                          return next;
                        })}>
                          {skip ? "replace after all" : "leave this one"}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {view.changed.some((c) => c.delivered || c.released) && (
                <p className="inc-note">
                  Taking these keeps the old versions and their feedback — the newer one becomes
                  the paper of record.
                </p>
              )}

              {/* A READ NOBODY DECLARED HERE. A Drive folder linked to this collection confirms
                  its own reads — attaching the folder is the assertion, so there is nothing left
                  to agree to. What can still reach this list is a folder read by a command line
                  against a path on somebody's machine: no link, so no earlier agreement, and the
                  console has no way to stand in for one. It used to say "waiting for you to
                  confirm the folder", which named an action a teacher could not take. */}
              {view.awaiting_confirmation.map((h) => (
                <p key={h.manifest_id} className="inc-note">
                  {h.files} file{h.files === 1 ? "" : "s"} in {h.folder} were read from a path on a
                  machine rather than from a folder linked here, so they cannot be taken. Link the
                  Drive folder to this collection and check again.
                </p>
              ))}

              {total > 0 && (
                <div className="inc-actions">
                  {/* WHAT IT DOES, not what we call it internally. "Take" is the word for binding
                      a file to a student and a task, which is ours and not a teacher's — and the
                      button beside it, "Not now", decided nothing at all: it closed the panel, and
                      pressing check again brought the same papers straight back. A control that
                      looks like a choice and is a dismissal teaches people not to trust the
                      other one. */}
                  {/* COUNTS WHAT IT WILL DO, not what arrived. A button offering twenty papers
                      that takes eighteen is the same defect as a folder reporting one of twenty. */}
                  <button className="btn primary" disabled={busy || taking === 0} onClick={take}>
                    {busy ? "Adding…"
                      : taking === 0 ? "Every changed paper is being left alone"
                      : `Add ${taking} paper${taking === 1 ? "" : "s"} to this cohort`}
                  </button>
                  <button className="btn" disabled={busy} onClick={() => setView(null)}>
                    Close
                  </button>
                </div>
              )}
            </>
          )}

          {/* Outside the branch, because it is an account of the READ rather than of the work in
              it — and the case where it matters most is the one where there is no work. */}
          <SkippedFiles files={view.not_taken ?? []} open={showSkipped}
                        onToggle={() => setShowSkipped((o) => !o)} />
        </div>
      )}
    </div>
  );
}


/** What was in the folder and will not become a paper.
 *
 *  BELOW A RULE AND CLOSED. It is an account of what happened, not a list of things to do — but
 *  leaving it out entirely is what made "1 new" read as a complete answer for a folder of fifteen
 *  files. Counted in the summary line so the number is visible without opening anything.
 *
 *  TWO GROUPS, BECAUSE THEY DIFFER. A PDF or a photograph is a file this cannot open at all. The
 *  assignment prompt is a file it opened and recognised — usually a Google Doc, so filing it under
 *  "not a Google Doc or Word" would be false, and a teacher looking for why their student's work
 *  was skipped would be sent to the wrong answer.
 */
/** The reason, in words rather than in our vocabulary.
 *
 *  Unreadable files carry prose already — `extract` raises with the sentence a person should read
 *  (".pdf is not a format this can read yet"). The two classifier codes do not, and
 *  `looks_like_the_assignment` on the screen is the system talking to itself. Where the heading
 *  already says it, nothing is added: a reason repeated on every row is noise.
 */
function why(f: { status: string; why: string | null }): string | null {
  if (f.status !== "unreadable") {
    return f.why === "system_file" ? "a system file, not a document" : null;
  }
  return f.why;
}


function SkippedFiles({ files, open, onToggle }: {
  files: { file_id: string; name: string; status: string; why: string | null }[];
  open: boolean;
  onToggle: () => void;
}) {
  if (files.length === 0) return null;
  const unreadable = files.filter((f) => f.status === "unreadable");
  const prompts = files.filter((f) => f.status !== "unreadable");

  return (
    <div className="inc-skipped">
      <hr className="inc-rule" />
      <button className="btn-link" aria-expanded={open} onClick={onToggle}>
        {open ? "▾" : "▸"} {files.length} file{files.length === 1 ? "" : "s"} not taken as student
        work
      </button>
      {open && (
        <div className="inc-skipped-body">
          {unreadable.length > 0 && (
            <>
              <h4>Files that are not Google Docs or Word</h4>
              <ul className="inc-list">
                {unreadable.map((f) => (
                  <li key={f.file_id}>
                    <span className="inc-name">{f.name}</span>
                    {/* The reason the reader came for: why THIS file. */}
                    {why(f) && <span className="inc-quiet"> — {why(f)}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
          {prompts.length > 0 && (
            <>
              <h4>Read as the assignment, not as an answer to it</h4>
              <ul className="inc-list">
                {prompts.map((f) => (
                  <li key={f.file_id}>
                    <span className="inc-name">{f.name}</span>
                    {why(f) && <span className="inc-quiet"> — {why(f)}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
